import { createTheme, Theme } from "@mui/material";
import { colors, brand, BrandTokens } from "./ColorsForTheam";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";

function cssVarsFromTokens(t: BrandTokens): Record<string, string> {
  return {
    "--g-bg": t.warmBg,
    "--g-surface": t.surface,
    "--g-surface-2": t.surface2,
    "--g-surface-3": t.surface3,
    "--g-border": t.border,
    "--g-border-strong": t.borderStrong,
    "--g-text": t.dark,
    "--g-text-2": t.textSecondary,
    "--g-text-3": t.textTertiary,
    "--g-accent": t.orange,
    "--g-accent-hover": t.accentHover,
    "--g-accent-tint": t.tint50,
    "--g-accent-tint-2": t.tint100,
    "--g-blue": t.blue,
    "--g-blue-tint": t.blueTint,
    "--g-success": t.success,
    "--g-success-tint": t.successTint,
    "--g-warning": t.warning,
    "--g-warning-tint": t.warningTint,
    "--g-danger": t.danger,
    "--g-danger-tint": t.dangerTint,
    "--g-shadow": t.shadowWhite,
    "--g-shadow-lg": t.shadowLg,
    "--g-shadow-accent": t.shadowOrange,
    "--g-shadow-blue": t.shadowBlue,
    "--g-ring": t.ring,
    "--g-overlay": t.overlay,
    "--g-gradient": t.orangeGradient,
    "--g-gradient-tile": t.gradientTile,
    "--g-gradient-avatar": t.gradientAvatar,
  };
}

export function createGamixterTheme(): Theme {
  const t = brand;
  const vars = cssVarsFromTokens(t);

  return createTheme({
    palette: {
      background: {
        default: t.warmBg,
        paper: t.surface,
      },
      primary: {
        main: t.orange,
      },
      secondary: {
        main: t.blue,
      },
      error: {
        main: t.danger,
      },
      warning: {
        main: t.warning,
      },
      success: {
        main: t.success,
      },
      action: {
        hover: t.tint50,
      },
      divider: t.border,
      text: {
        primary: t.dark,
        secondary: t.textSecondary,
        disabled: t.textTertiary,
      },
    },
    typography: {
      fontFamily: "Inter, sans-serif",
      h1: {
        color: t.dark,
        fontSize: "26px",
        fontWeight: 800,
        lineHeight: "1.15",
        letterSpacing: "-1px",
      },
      h2: {
        color: t.dark,
        fontSize: "19px",
        fontWeight: 800,
        lineHeight: "1.2",
        letterSpacing: "-0.5px",
      },
      h3: {
        color: t.dark,
        fontSize: "15px",
        fontWeight: 600,
        lineHeight: "21px",
      },
      h4: {
        color: t.dark,
        fontSize: "16px",
        fontWeight: 600,
        lineHeight: "22px",
      },
      body1: {
        fontSize: "14px",
        fontWeight: 400,
        lineHeight: "1.55",
        color: t.dark,
      },
      subtitle1: {
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: "16px",
        color: t.textSecondary,
      },
      subtitle2: {
        fontSize: "12px",
        fontWeight: 400,
        lineHeight: "20px",
        color: t.textSecondary,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": vars,
          body: {
            overflowY: "scroll",
            backgroundColor: t.warmBg,
            color: t.dark,
          },
          "input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button":
            {
              WebkitAppearance: "none",
              margin: 0,
            },
          "input[type=number]": {
            MozAppearance: "textfield",
          },
          "::selection": {
            background: "#FED7AA",
            color: t.dark,
          },
          a: {
            color: t.orange,
            textDecoration: "none",
            "&:hover": {
              color: t.accentHover,
            },
          },
          "@keyframes uk-spin": {
            to: { transform: "rotate(360deg)" },
          },
          "@keyframes uk-shimmer": {
            "0%": { backgroundPosition: "-400px 0" },
            "100%": { backgroundPosition: "400px 0" },
          },
          "@keyframes uk-pop": {
            from: { transform: "translateY(10px) scale(.98)", opacity: 0 },
            to: { transform: "translateY(0) scale(1)", opacity: 1 },
          },
          // For modals centered via translate(-50%,-50%): keeps centering during the animation
          "@keyframes uk-pop-center": {
            from: { transform: "translate(-50%, calc(-50% + 10px)) scale(.98)", opacity: 0 },
            to: { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
          },
          "@keyframes uk-toast-in": {
            from: { transform: "translateY(16px)", opacity: 0 },
            to: { transform: "translateY(0)", opacity: 1 },
          },
          "@keyframes uk-fade-in": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          outlined: {
            borderColor: t.border,
          },
          elevation1: {
            boxShadow: t.shadowWhite,
            borderRadius: 22,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: "999px",
            fontWeight: 600,
            fontSize: "13px",
            height: "auto",
          },
          outlined: {
            borderColor: t.border,
            backgroundColor: t.surface,
            color: t.textSecondary,
          },
          filled: {
            backgroundColor: t.tint50,
            color: t.orange,
          },
          label: {
            paddingLeft: 13,
            paddingRight: 13,
            paddingTop: 7,
            paddingBottom: 7,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            "&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${t.borderStrong}`,
            },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${t.accentHover}`,
            },
            "&.MuiOutlinedInput-root .MuiInputBase-input:focus ~ .MuiOutlinedInput-notchedOutline":
              {
                border: `1px solid ${t.orange}`,
              },
            "&.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
              {
                border: `1px solid ${t.danger}`,
              },
          },
          input: {
            "&:-internal-autofill-selected": {
              background: t.surface,
              backgroundColor: `${t.surface} !important`,
              appearance: "none",
            },
            "&:focus": {},
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            background: t.surface,
            borderRadius: 12,
            transition: "all .15s",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${t.orange}`,
              boxShadow: `0 0 0 4px ${t.ring}`,
            },
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${t.danger}`,
            },
            "&.Mui-error": {
              background: t.dangerTint,
            },
          },
          input: {
            padding: "10px 14px",
            transition: "all .15s",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "1.5",
            borderRadius: 12,
            background: "transparent",
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginTop: 0,
            fontSize: "12px",
            fontWeight: 400,
            lineHeight: "20px",
            "&.Mui-error": {
              color: t.danger,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "initial" as const,
            borderRadius: "12px",
            fontWeight: 600,
            fontSize: "14px",
            lineHeight: "1",
          },
          contained: {
            padding: "11px 20px",
            background: t.orange,
            boxShadow: "none",
            "&:hover": {
              background: t.accentHover,
              boxShadow: "none",
            },
            "&:disabled": {
              background: t.surface2,
              color: t.textTertiary,
              border: `1px solid ${t.border}`,
              cursor: "not-allowed",
              pointerEvents: "auto",
            },
          },
          containedSecondary: {
            background: t.blue,
            boxShadow: t.shadowBlue,
            "&:hover": {
              background: "#1E40AF",
              boxShadow: t.shadowBlue,
            },
          },
          containedError: {
            background: t.dangerTint,
            color: t.danger,
            border: "1px solid transparent",
            boxShadow: "none",
            "&:hover": {
              background: "#F7DBD3",
              borderColor: t.danger,
            },
          },
          outlined: {
            background: t.surface,
            border: `1px solid ${t.borderStrong}`,
            color: t.dark,
            "&:hover": {
              background: t.surface2,
              border: `1px solid ${t.borderStrong}`,
            },
          },
          text: {
            color: t.orange,
            "&:hover": {
              background: t.tint50,
            },
          },
          sizeSmall: {
            padding: "7px 12px",
            fontSize: "13px",
            borderRadius: "10px",
          },
          sizeLarge: {
            padding: "14px 26px",
            fontSize: "16px",
            borderRadius: "14px",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            boxShadow: t.shadowLg,
            border: `1px solid ${t.border}`,
            borderRadius: 14,
            background: t.surface,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "10px 13px",
            borderRadius: 9,
            margin: "2px 6px",
            "&:hover": {
              background: t.surface2,
            },
            "&:active": {
              background: t.tint100,
            },
            "&.Mui-selected": {
              background: t.tint50,
              color: t.orange,
              fontWeight: 600,
              "&:hover": {
                background: t.tint100,
              },
            },
            "&.Mui-disabled": {
              opacity: 0.56,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            "& svg": {
              width: 20,
              height: 20,
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            fontWeight: 400,
            lineHeight: "22px",
            color: t.dark,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 22,
            boxShadow: t.shadowWhite,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            background: t.surface2,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "13px 20px",
            borderColor: t.border,
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: t.dark,
            fontSize: "14px",
            textAlign: "center",
          },
          head: {
            color: t.textTertiary,
            fontSize: "11.5px",
            fontWeight: 700,
            lineHeight: "1",
            letterSpacing: "0.8px",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            border: "none",
            padding: "12px 20px",
            background: t.surface2,
            textAlign: "center",
            "& .MuiTypography-root": {
              fontSize: "inherit",
              fontWeight: "inherit",
              letterSpacing: "inherit",
              color: "inherit",
              lineHeight: "inherit",
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: "background .12s",
            "&:hover": {
              background: t.surface2,
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            padding: "16px 14px",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "22px",
            textTransform: "initial",
            color: t.textTertiary,
            border: "none",
            minHeight: 40,
            transition: "color .15s",
            "&.Mui-selected": {
              color: t.orange,
              background: "transparent",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${t.border}`,
            minHeight: 40,
          },
          indicator: {
            backgroundColor: t.orange,
            height: 2,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            transform: "none",
            position: "relative",
            marginBottom: 8,
            fontWeight: 600,
            fontSize: "13px",
            color: t.textSecondary,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            outline: "none",
            boxSizing: "border-box",
            "& .MuiOutlinedInput-notchedOutline legend": {
              display: "none",
            },
            "&.Mui-focused": {
              border: `1px solid ${t.orange}`,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              height: "38px",
              top: 0,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
          select: {
            minHeight: "38px",
            boxSizing: "border-box",
            padding: "8px 32px 8px 12px",
            "&:focus-visible": {
              border: "none",
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              padding: 0,
              height: "38px",
            },
            "& .MuiOutlinedInput-root": {
              padding: 0,
              background: t.surface,
            },
          },
          inputRoot: {
            padding: 0,
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            "&::after,&::before": {
              display: "none",
            },
          },
          underline: {
            border: "none",
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: t.shadowWhite,
            borderRadius: "18px !important",
            border: `1px solid ${t.border}`,
            background: t.surface,
            "&::before": {
              display: "none",
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            background: t.surface2,
            borderRadius: "18px",
            color: t.dark,
            fontWeight: 600,
            padding: "4px 20px",
            "&.Mui-expanded": {
              borderRadius: "18px 18px 0 0",
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          asterisk: {
            display: "none",
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: t.orange,
            padding: "8px 16px",
            color: t.orange,
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "22px",
            "&:hover": {
              backgroundColor: t.tint50,
            },
            "&.Mui-selected": {
              color: "#ffffff",
              backgroundColor: t.orange,
              "&:hover": {
                backgroundColor: t.accentHover,
              },
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            boxShadow: t.shadowOrange,
            borderRadius: "12px",
          },
          lastButton: {
            borderLeftColor: t.orange,
          },
          middleButton: {
            borderColor: t.orange,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 22,
            boxShadow: t.shadowLg,
            background: t.surface,
            animation: "uk-pop .18s ease-out",
          },
          // Width follows the dialog's own `maxWidth` (a confirm is `xs`, a form `sm`/`md`) — a
          // single cap here squeezed every form, wide ones included, into a confirm's width.
          paperWidthXs: { maxWidth: 520 },
          paperWidthSm: { maxWidth: 720 },
          paperWidthMd: { maxWidth: 1000 },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontSize: "20px",
            fontWeight: 800,
            letterSpacing: "-0.7px",
            padding: "28px 28px 0",
            color: t.dark,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: "22px 28px",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: "0 28px 28px",
            gap: 12,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            maxWidth: 420,
            background: t.surface,
            borderLeft: `1px solid ${t.border}`,
            boxShadow: t.shadowLg,
            animation: "uk-fade-in .15s ease-out",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 46,
            height: 26,
            padding: 0,
          },
          switchBase: {
            padding: 3,
            "&.Mui-checked": {
              transform: "translateX(20px)",
              color: "#fff",
              "& + .MuiSwitch-track": {
                backgroundColor: t.orange,
                opacity: 1,
              },
            },
          },
          thumb: {
            width: 20,
            height: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          },
          track: {
            borderRadius: 999,
            backgroundColor: t.borderStrong,
            opacity: 1,
            transition: "background-color .18s",
          },
        },
      },
      // Switch has padding: 0 (custom track), so the default gap to the label is gone — restore it
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            "&:has(.MuiSwitch-root)": {
              marginLeft: 0,
              gap: 10,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: t.borderStrong,
            "&.Mui-checked": {
              color: t.orange,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: t.borderStrong,
            "&.Mui-checked": {
              color: t.orange,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            background: t.dark,
            color: "#FBF3EC",
            fontSize: "13px",
            fontWeight: 500,
            borderRadius: 10,
            padding: "8px 14px",
            boxShadow: t.shadowLg,
          },
          arrow: {
            color: t.dark,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: t.surface2,
            borderRadius: 8,
            "&::after": {
              background: `linear-gradient(90deg, transparent, ${t.surface3}, transparent)`,
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          standardSuccess: {
            backgroundColor: t.successTint,
            color: t.success,
          },
          standardError: {
            backgroundColor: t.dangerTint,
            color: t.danger,
          },
          standardWarning: {
            backgroundColor: t.warningTint,
            color: t.warning,
          },
          standardInfo: {
            backgroundColor: t.blueTint,
            color: t.blue,
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: t.overlay,
          },
        },
      },
    },
  });
}

// Backwards compatibility export for any code that imports GamixterTheam directly
export const GamixterTheam = createGamixterTheme();
